import type { KorrekturOverviewDTO } from "../korrekturOverview.types";
import { KorrekturStepStatus } from "../korrekturOverview.types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, UserPen } from "lucide-react";

// Status configuration for individual exam cards
const EXAM_STATUS_CONFIG = {
	LOCKED: {
		label: "Gesperrt",
		sublabel: "Warte auf Vorkorrektur",
		bgColor: "bg-gray-50",
		borderColor: "border-gray-300",
		iconColor: "text-gray-400",
	},
	NOT_STARTED: {
		label: "Nicht gestartet",
		bgColor: "bg-white",
		borderColor: "border-blue-300",
		iconColor: "text-blue-400",
	},
	IN_PROGRESS: {
		label: "In Bearbeitung",
		bgColor: "bg-white",
		borderColor: "border-orange-300",
		iconColor: "text-orange-400",
	},
	COMPLETED: {
		label: "Abgeschlossen",
		bgColor: "bg-green-50",
		borderColor: "border-green-300",
		iconColor: "text-green-400",
	},
	WAITING: {
		label: "Warten",
		bgColor: "bg-gray-50",
		borderColor: "border-gray-300",
		iconColor: "text-gray-400",
	},
} as const;

/**
 * ExamCard component for displaying exam information
 * @param klausur - The exam data
 * @param onClick - The function to call when the card is clicked
 * @returns The ExamCard component
 */
export default function ExamCard({
	klausur,
	onClick,
}: {
	klausur: KorrekturOverviewDTO;
	onClick: () => void;
}) {
	const getStatusConfig = (status: KorrekturStepStatus, locked: boolean) => {
		switch (status) {
			case "NOT_STARTED":
				return locked
					? EXAM_STATUS_CONFIG.LOCKED
					: EXAM_STATUS_CONFIG.NOT_STARTED;
			case "IN_PROGRESS":
				return locked
					? EXAM_STATUS_CONFIG.LOCKED
					: EXAM_STATUS_CONFIG.IN_PROGRESS;
			case "COMPLETED":
				return EXAM_STATUS_CONFIG.COMPLETED; // Always show completed, even if locked
			default:
				return locked
					? EXAM_STATUS_CONFIG.LOCKED
					: EXAM_STATUS_CONFIG.NOT_STARTED;
		}
	};

	const currentUserStep = klausur.korrekturSteps.find(
		(step) => step.isCurrentUser,
	);
	const statusConfig = getStatusConfig(
		currentUserStep?.status || KorrekturStepStatus.NOT_STARTED,
		currentUserStep?.locked || false,
	);

	// Check if the card should be clickable
	const isClickable = !(
		currentUserStep?.status === KorrekturStepStatus.NOT_STARTED &&
		currentUserStep?.locked
	);

	const handleClick = () => {
		if (isClickable) {
			onClick();
		}
	};

	return (
		<Card
			className={`transition-all border-2 h-32 relative ${statusConfig.bgColor} ${statusConfig.borderColor} ${
				isClickable
					? "cursor-pointer hover:shadow-md"
					: "cursor-not-allowed opacity-75"
			}`}
			onClick={handleClick}
		>
			<CardHeader className="flex flex-row justify-between items-center">
				<div>
					<CardTitle>{statusConfig.label}</CardTitle>
					{"sublabel" in statusConfig && (
						<div className="text-xs text-gray-500 font-normal">
							{statusConfig.sublabel}
						</div>
					)}
				</div>
				<div className="flex justify-between items-start">
					<div className="flex gap-1">
						{klausur.korrekturSteps.map((step) => {
							const IconComponent = step.isCurrentUser ? UserPen : User;
							return (
								<IconComponent
									className={`${step.isCurrentUser ? "w-5 h-5" : "w-4 h-4"} ${
										step.status === "NOT_STARTED"
											? EXAM_STATUS_CONFIG.NOT_STARTED.iconColor
											: step.status === "IN_PROGRESS"
												? EXAM_STATUS_CONFIG.IN_PROGRESS.iconColor
												: step.status === "COMPLETED"
													? EXAM_STATUS_CONFIG.COMPLETED.iconColor
													: EXAM_STATUS_CONFIG.NOT_STARTED.iconColor
									}`}
									key={step.stepNr}
								/>
							);
						})}
					</div>
				</div>
			</CardHeader>
			<CardContent className="h-full flex flex-col justify-between">
				<div className="space-y-1">
					<div className="text-xs text-gray-600 truncate">
						ID: {klausur.internalIdentifier}
					</div>
					<div className="text-xs text-gray-600">{klausur.title}</div>
				</div>
			</CardContent>
		</Card>
	);
}
